import React, { useCallback, useEffect, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { onErrorShowMessage } from '~src/errors';
import getGraphQlClient from '~graphql/getGraphQlClient';
import { unmarshalGraphqlResponse } from '~graphql/utils';
import { FIND_CUSTOM_EDITOR_TEMPLATES } from '~graphql/queries/editorTemplates';
import { FEATURES, isFeatureEnabled } from '~src/features';
import { isLoggedUserSuperUser } from '~src/user';
import RecentTemplateItem from './RecentTemplateItem';

const fetchCustomTemplatesPage = async (orgId, after) => {
  const graphQlClient = await getGraphQlClient();
  const response = await graphQlClient.query({
    fetchPolicy: 'network-only',
    variables: { limit: 100, orgId, after },
    query: FIND_CUSTOM_EDITOR_TEMPLATES,
  });

  return unmarshalGraphqlResponse(response.data, 'findEditorTemplates');
};

const fetchAllCustomTemplates = async (orgId) => {
  let after;
  let hasNextPage = true;
  const templates = [];

  while (hasNextPage) {
    const result = await fetchCustomTemplatesPage(orgId, after);
    templates.push(...(result?.edges || []));
    hasNextPage = result?.pageInfo?.hasNextPage === true;
    after = result?.pageInfo?.endCursor;
  }

  return templates;
};

function RecentTemplates({ orgId }) {
  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [allowDelete, setAllowDelete] = useState(false);
  const countContainer = document.getElementById('recent-templates-count');

  const loadTemplates = useCallback(async () => {
    if (!orgId) {
      setItems([]);
      setTotalCount(0);
      setAllowDelete(false);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const [templates, isSuperUser] = await Promise.all([
        fetchAllCustomTemplates(orgId),
        isLoggedUserSuperUser(),
      ]);
      const canDeleteTemplates =
        isSuperUser || (await isFeatureEnabled(FEATURES.DELETE_TEMPLATES));
      setItems(templates);
      setTotalCount(templates.length);
      setAllowDelete(canDeleteTemplates);
    } catch (err) {
      console.error(err);
      onErrorShowMessage();
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  return (
    <>
      {countContainer &&
        !loading &&
        totalCount !== null &&
        createPortal(
          <span
            aria-label={
              totalCount === 1
                ? '1 modello personalizzato'
                : `${totalCount} modelli personalizzati`
            }
            className="badge badge-pill bg-color-existing-canvas text-white ml-2 align-middle"
          >
            {totalCount}
          </span>,
          countContainer
        )}
      {loading && (
        <Spinner
          className="my-4 mx-auto text-dark"
          animation="border"
          size="lg"
          variant="info"
        />
      )}
      {!loading && items.length === 0 && (
        <h5 className="my-5 mx-auto text-dark">Nessun modello trovato</h5>
      )}
      {items.map((template) => (
        <RecentTemplateItem
          key={template.objectId}
          template={template}
          allowDelete={allowDelete}
          onTemplatesChanged={loadTemplates}
        />
      ))}
    </>
  );
}

RecentTemplates.propTypes = {
  orgId: PropTypes.string,
};

export default RecentTemplates;
